import dayjs from "dayjs";
import { convert_to_comma_seperated_list } from "../util";
import { LayerFilter } from "./constants";


// * Method types compatible with backend
export const FILTER_METHODS = {
    IN: "in", // * Meta text with options defined or color fields
    NOT_IN: "not_in", // * Meta text with options defined
    STARTS_WITH: "starts_with", // * Meta text no options, search first characters (becauseo of index)
    DATE_FROM: "date_from", // * Meta date from
    DATE_TO: "date_to", // * Meta date to
    NUMBER_RANGE_MIN: "number_range_min", // * Meta number range min (metrics)
    NUMBER_RANGE_MAX: "number_range_max", // * Meta number range max (metrics)
    IN_ANY_KEY: "in_any_key", // * Any of meta keys is equal to value e.g. advance_ripening_qc_status == "draft" or intake_qc_status == "draft"
    IS_SET: "is_set", // * Meta key is not set or empty
    NOT_CURRENT_FACILITY: "not_current_facility", // * Meta value (a facility) is not the layer's current facility, or missing

};

// * Where a tab's static meta filter looks for the meta field
export const META_FILTER_SOURCES = {
    LAYER_META: "layer_meta", // * The layer's own meta (falls back to parent meta, see backend)
    PARENT_META: "parent_meta", // * Meta of any active parent
    PARENT_LAYER: "parent_layer", // * Column of the active parents, only "type" (in: has a parent of type, not_in: has none)
};

// * Method types compatible with frontend
export const FILTER_METHOD_TYPES = {
    TEXT: "text",
    DATE_RANGE: "date_range",
    NUMBER_RANGE: "number_range",
    COLOR_CODE: "color_code",
};


// * Pattern matches layer_meta.check_created and layer_meta.<location>_check_created
// eslint-disable-next-line security/detect-unsafe-regex
const CHECK_DATE_FIELD_RE = /^layer_meta\.((?:[^.]+_)?check_created)$/;

// * Detect check-date meta filter items and promote them to root params (check_created_from/to, check_location).
// * Items translated here are removed from meta_filter to avoid double-filtering.
function extractCheckDateParams(metaItems: Array<{ method: string; field: string; value: any }>): {
    check_created_from?: string;
    check_created_to?: string;
    check_location?: string;
    remainingItems: Array<{ method: string; field: string; value: any }>;
} {
    let check_created_from: string | undefined;
    let check_created_to: string | undefined;
    let check_location: string | undefined;
    const remainingItems: Array<{ method: string; field: string; value: any }> = [];

    metaItems.forEach((item) => {
        const match = CHECK_DATE_FIELD_RE.exec(item.field);
        if (match && (item.method === "date_from" || item.method === "date_to") && item.value) {
            const fieldSuffix = match[1]; // e.g., "check_created" or "intake_check_created"
            if (fieldSuffix !== "check_created") {
                // location-prefixed field e.g. "intake_check_created" → location = "intake"
                const location = fieldSuffix.replace(/_check_created$/, "");
                if (!check_location) check_location = location;
            }
            if (item.method === "date_from") {
                check_created_from = dayjs(item.value).startOf("day").toISOString();
            } else {
                check_created_to = dayjs(item.value).endOf("day").toISOString();
            }
            // translated to root param — do not add to remainingItems
        } else {
            remainingItems.push(item);
        }
    });

    return { check_created_from, check_created_to, check_location, remainingItems };
}
export function prepareParams(filter: LayerFilter = {}) {
    const { meta_filter, ...params } = filter;
    // * Convert frontend object to array, filter empty/invalid filters, keep only fields needed for backend (method + field required), then stringify
    const isValidMetaItem = (i: any) => i != null && typeof i === "object" && "method" in i && "field" in i && i.method != null && i.field != null;
    const metaItems = Object.values(meta_filter || {}).filter(isValidMetaItem).map(({ method, field, value }: any) => ({ method, field, value }));

    // * Translate check-date meta filter items to root params
    const { check_created_from: metaCheckFrom, check_created_to: metaCheckTo, check_location: metaCheckLocation, remainingItems } = extractCheckDateParams(metaItems);

    const preparedParams = {
        ...params,
        meta_filter: JSON.stringify(remainingItems),
        stage: convert_to_comma_seperated_list(filter.stage),
        select_fields: convert_to_comma_seperated_list(filter.select_fields),
        check_location: convert_to_comma_seperated_list(filter.check_location) || metaCheckLocation,
        assigned_team: convert_to_comma_seperated_list(filter.assigned_team),
        fruit_type: convert_to_comma_seperated_list(filter.fruit_type),
        facility: convert_to_comma_seperated_list(filter.facility),
        facility_filter_meta_fields: convert_to_comma_seperated_list(filter.facility_filter_meta_fields),
        check_created_from: (params as any).check_created_from || metaCheckFrom,
        check_created_to: (params as any).check_created_to || metaCheckTo,
    } as any;
    return preparedParams;
}
